<template>
  <div>
    <snackbar-comp :SnackbarComponent="SnackbarComponent" />
    <v-dialog v-model="internalDialog" max-width="600" persistent scrollable>
      <v-card class="rounded-lg border d-flex flex-column">
        <v-card-title
          class="d-flex align-center bg-primary text-white pa-2 text-subtitle-1 font-weight-bold flex-shrink-0"
        >
          <v-icon class="mr-2" size="small">mdi-flag-variant</v-icon>
          Review Poll Report
          <v-spacer></v-spacer>
          <v-btn variant="text" size="small" icon @click="closeDialog">
            <v-icon>mdi-close-thick</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 pb-2 px-4 flex-grow-1 overflow-y-auto">
          <div class="d-flex justify-space-between align-start mb-3">
            <div>
              <div
                class="text-caption text-grey-darken-1 font-weight-bold text-uppercase"
              >
                Reported On
              </div>
              <div class="text-body-2 font-weight-medium">
                <v-icon size="x-small" class="mr-1">mdi-calendar-clock</v-icon>
                {{ formatDate(request?.created_on) }}
              </div>
            </div>
            <v-chip
              size="small"
              :color="statusColor"
              class="font-weight-bold text-uppercase"
              variant="flat"
            >
              {{ request?.workflow_status }}
            </v-chip>
          </div>

          <div
            class="text-error pa-3 rounded-lg border-sm mb-4"
          >
            <div
              class="text-caption font-weight-bold text-uppercase mb-1 d-flex align-center"
            >
              <v-icon size="small" class="mr-1">mdi-comment-alert</v-icon>
              Reporter's Comment
            </div>
            <div class="text-body-2 font-italic">
              "{{ request?.comment || "No reason provided." }}"
            </div>
          </div>

          <v-divider class="mb-4"></v-divider>

          <div
            class="text-caption font-weight-bold text-grey-darken-1 text-uppercase mb-2"
          >
            Reported Poll Details
          </div>
          <v-card
            variant="outlined"
            class="bg-grey-lighten-5 rounded-lg border-sm"
          >
            <v-card-item class="pb-1 pt-3">
              <div class="d-flex justify-space-between align-center mb-1">
                <!-- <v-chip
                  size="x-small"
                  color="primary"
                  variant="tonal"
                  class="font-weight-bold"
                >
                  {{ pollDetails?.category || "Uncategorized" }}
                </v-chip> -->
                <span
                  class="text-caption font-weight-medium text-grey-darken-1"
                >
                  ID: {{ pollDetails?.poll_display_id }}
                </span>
              </div>
              <v-card-title
                class="text-subtitle-1 font-weight-bold text-wrap leading-tight mt-1"
              >
                {{ pollDetails?.title }}
              </v-card-title>
              <div
                class="text-caption font-weight-bold text-grey-darken-1 mb-1 mt-3"
              >
                Poll Options:
              </div>
              <div class="d-flex flex-wrap gap-2">
                <v-chip
                  v-for="opt in pollDetails?.options"
                  :key="opt.optionId"
                  size="small"
                  variant="outlined"
                  color="grey-darken-3"
                >
                  {{ opt.text }}
                </v-chip>
              </div>
            </v-card-item>

            <v-card-text class="pb-3 pt-2">
              <div
                class="text-caption text-grey-darken-2 d-flex align-center mb-2"
              >
                <v-icon size="x-small" class="mr-1">mdi-bullseye-arrow</v-icon>
                <span class="font-weight-medium"
                  >{{ pollDetails?.target_type }} /
                  {{ pollDetails?.target_value }}</span
                >
                <span class="ml-2">(Age: {{ pollDetails?.age_group }})</span>
              </div>

              <v-row
                dense
                class="text-caption text-grey-darken-2 mb-2 bg-white rounded border-sm mx-0 pa-1"
              >
                <v-col cols="12" sm="6" class="d-flex align-center">
                  <strong>Start:</strong> &nbsp;
                  {{ formatDate(pollDetails?.start_time) }}
                </v-col>
                <v-col cols="12" sm="6" class="d-flex align-center justify-end">
                  <strong>End:</strong> &nbsp;
                  {{ formatDate(pollDetails?.end_time) }}
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions class="pa-3 bg-grey-lighten-4 border-t flex-shrink-0">
          <v-spacer></v-spacer>
          <v-btn
            variant="plain"
            size="small"
            class="text-none px-3"
            @click="closeDialog"
          >
            Close
          </v-btn>

          <template v-if="request?.workflow_status === 'PENDING'">
            <v-btn
              color="grey-darken-2"
              variant="outlined"
              prepend-icon="mdi-close-circle"
              size="small"
              class="text-none px-4 bg-white"
              :loading="loadingReject"
              @click="handleApproveReject('REJECT')"
            >
              Ignore Report
            </v-btn>
            <v-btn
              color="warning"
              variant="flat"
              prepend-icon="mdi-delete-alert"
              size="small"
              :loading="loadingAbort"
              class="text-none px-4 ml-2"
              @click="handleApproveReject('ABORT')"
            >
              Abort Poll
            </v-btn>
            <v-btn
              color="error"
              variant="flat"
              prepend-icon="mdi-delete-alert"
              size="small"
              class="text-none px-4 ml-2"
              :loading="loadingDelete"
              @click="handleApproveReject('DELETE')"
            >
              Delete Poll
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { acknowledgePollReport } from "@/graphql/mutations";
import SnackbarComp from "@/components/Extras/SnackbarComp.vue";
import { generateClient } from "aws-amplify/api";

const client = generateClient();

export default {
  name: "PollReportDialog",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    SnackbarComp,
  },
  model: {
    prop: "dialog",
    event: "update:dialog",
  },
  emits: ["update:dialog", "clicked", "approve", "reject"],
  data() {
    return {
      loadingDelete: false,
      loadingReject: false,
      loadingAbort: false,
      SnackbarComponent: {
        SnackbarVmodel: false,
        SnackbarText: "",
        SnackbarColor: "success",
      },
    };
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        
      }
    },
  },
  computed: {
    internalDialog: {
      get() {
        return this.dialog;
      },
      set(val) {
        this.$emit("update:dialog", val);
      },
    },
    pollDetails() {
      // Safely extract the nested poll details
      return this.request?.poll_details || {};
    },
    statusColor() {
      switch (this.request?.workflow_status) {
        case "PENDING":
          return "warning";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "error";
        default:
          return "grey";
      }
    },
  },
  methods: {
    closeDialog() {
      this.internalDialog = false;
    },
    async handleApproveReject(action) {
      try {
        if (action === "ABORT") {
          this.loadingAbort = true;
        } else if (action === "REJECT") {
          this.loadingReject = true;
        } else if (action === "DELETE") {
          this.loadingDelete = true;
        }
        let result = await client.graphql({
          query: acknowledgePollReport,
          variables: {
            input: {
              user_id: this.$store.getters.get_current_user_details.user_id,
              pollId: this.request.pollId,
              workflow_id: this.request.workflow_id,
              action_type: action,
            },
          },
        });

        var ResultObj = JSON.parse(result.data.acknowledgePollReport);
        if (ResultObj.status == 200 || ResultObj.status === "SUCCESS") {
          this.SnackbarComponent = {
            SnackbarVmodel: true,
            SnackbarText: ResultObj.status_message,
            SnackbarColor: "success",
          };
          this.$emit("clicked", 2);
          this.closeDialog();
        }
        this.loadingAbort = false;
        this.loadingReject = false;
        this.loadingDelete = false;
      } catch (error) {
        this.loadingAbort = false;
        this.loadingReject = false;
        this.loadingDelete = false;
        console.log("Error", error);
        this.SnackbarComponent = {
          SnackbarVmodel: true,
          SnackbarText:
            error.errors?.[0]?.message ||
            "An error occurred. Please try again.",
          SnackbarColor: "error",
        };
        this.closeDialog();
      }
    },
    handleApprove() {
      // Emits the full workflow payload back to the parent
      this.$emit("approve", this.request);
    },
    handleReject() {
      // Emits the full workflow payload back to the parent
      this.$emit("reject", this.request);
    },
    formatDate(epoch) {
      if (!epoch) return "N/A";
      const date = new Date(epoch);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    },
  },
};
</script>

<style scoped>
.border-sm {
  border: 1px solid #e0e0e0;
}
.border-t {
  border-top: 1px solid #e0e0e0;
}
.leading-tight {
  line-height: 1.3 !important;
}
.gap-2 {
  gap: 8px;
}
</style>
